"use client";

import React, { useState } from "react";
import styles from "./page.module.css";
// import styles from "./ContactPage.module.css";
import bookingService from "@/services/bookingService";
import crmApiService from "@/services/crmApiService";
import LazyGoogleMap from "../components/LazyGoogleMap";

type FormState = {
  fullName: string;
  phone: string;
  email: string;
  gender: string;
  occupation: string;
  dateOfBirth: string;
  service: string;
  note: string;
};

type FieldConfig = {
  key: keyof FormState;
  type: React.HTMLInputTypeAttribute;
  placeholder: string;
};

const CONTACT_FIELDS: FieldConfig[] = [
  { key: "fullName", type: "text", placeholder: "Họ tên" },
  { key: "phone", type: "tel", placeholder: "Số điện thoại (*)" },
  { key: "email", type: "email", placeholder: "Địa chỉ email" },
  { key: "occupation", type: "text", placeholder: "Nghề nghiệp" },
  { key: "dateOfBirth", type: "date", placeholder: "" },
];

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    fullName: "",
    phone: "",
    email: "",
    gender: "",
    occupation: "",
    dateOfBirth: "",
    service: "",
    note: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (!form.fullName || !form.phone || !form.email || !form.service) {
      setError("Vui lòng nhập Họ tên, Email, Số điện thoại và chọn Dịch vụ");
      return;
    }

    try {
      setSubmitting(true);
      
      // Gửi song song cả 2 API (không phụ thuộc lẫn nhau)
      const [internalResult, crmResult] = await Promise.allSettled([
        // Gửi đến API nội bộ
        bookingService.createPublic({
        fullName: form.fullName,
        phone: form.phone,
        email: form.email,
        service: form.service,
        note: form.note || undefined,
        }),
        // Gửi đến CRM API
        crmApiService.sendBooking({
          fullName: form.fullName,
          phone: form.phone,
          email: form.email,
          gender: form.gender || undefined,
          occupation: form.occupation || undefined,
          dateOfBirth: form.dateOfBirth || undefined,
          service: form.service,
          note: form.note || undefined,
        }),
      ]);

      // Kiểm tra kết quả
      if (internalResult.status === 'rejected') {
        console.error("Lỗi API nội bộ:", internalResult.reason);
      }
      if (crmResult.status === 'rejected') {
        console.warn("Lỗi CRM API:", crmResult.reason);
      }

      // Nếu ít nhất 1 trong 2 API thành công, hiển thị thành công
      const hasSuccess = 
        internalResult.status === 'fulfilled' || 
        crmResult.status === 'fulfilled';

      if (hasSuccess) {
      setSuccess("Đăng ký tư vấn thành công. Chúng tôi sẽ liên hệ sớm!");
      setForm({
        fullName: "",
        phone: "",
        email: "",
        gender: "",
        occupation: "",
        dateOfBirth: "",
        service: "",
        note: "",
      });
      } else {
        // Cả 2 đều lỗi
        const errorMsg = internalResult.status === 'rejected' 
          ? internalResult.reason?.message || "Gửi thông tin thất bại. Vui lòng thử lại."
          : "Gửi thông tin thất bại. Vui lòng thử lại.";
        setError(errorMsg);
      }
    } catch (error) {
      const fallbackMessage = "Gửi thông tin thất bại. Vui lòng thử lại.";
      setError(error instanceof Error ? error.message || fallbackMessage : fallbackMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.contactPage}>
      <div className={styles.contactContainer}>
        {/* Info Section */}
        <div className={styles.contactInfo}>
          <h2 className={styles.contactTitle}>LIÊN HỆ VỚI CHÚNG TÔI</h2>
          <div className={styles.contactContent}>
            <p>
              Địa chỉ:{" "}
              <a
                href="https://www.google.com/maps/search/?api=1&query=194+đường+số+7,+Khu+dân+cư+Trung+Sơn,+xã+Bình+Hưng,+huyện+Bình+Chánh,+TPHCM"
                target="_blank"
                rel="noopener noreferrer"
              >
                194 đường số 7, KDC Trung Sơn, xã Bình Hưng, TP. Hồ Chí Minh, Việt Nam.
              </a>
            </p>
            <p>
              Điện thoại: <a href="tel:0931494389">0931 494 389</a>
            </p>
            <p>
              Email: <a 
                href="https://mail.google.com/mail/?view=cm&fs=1&to=congngherongbien@gmail.com&tf=cm"
                target="_blank"
                rel="noopener noreferrer"
              >
                congngherongbien@gmail.com
              </a>
            </p>
            <p>
              Website: <a href="https://sdtc.vn" target="_blank" rel="noopener noreferrer">sdtc.vn</a>
            </p>
            <div className={styles.contactMap}>
              <LazyGoogleMap iframeClassName={styles.iframe} />
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className={styles.contactForm}>
          <h2 className={styles.contactTitle}>LIÊN HỆ TƯ VẤN</h2>
          <form className={styles.form}>
            {CONTACT_FIELDS.map((field) => (
              <div className={styles.formField} key={field.key}>
                <input
                  type={field.type}
                  value={form[field.key]}
                  placeholder={field.placeholder}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  className={styles.formInput}
                />
              </div>
            ))}

            <div className={styles.formField}>
              <select
                value={form.gender}
                onChange={(e) => handleChange("gender", e.target.value)}
                className={styles.formSelect}
              >
                <option value="">Chọn giới tính</option>
                <option value="Nam">Nam</option>
                <option value="Nữ">Nữ</option>
              </select>
            </div>

            <div className={styles.formField}>
              <select
                value={form.service}
                onChange={(e) => handleChange("service", e.target.value)}
                className={styles.formSelect}
              >
                <option value="">Chọn dịch vụ cần tư vấn (*)</option>
                <option value="Website">Website</option>
                <option value="App">App</option>
                <option value="Agency">Agency</option>
              </select>
            </div>

            <div className={styles.formField}>
              <textarea
                value={form.note}
                onChange={(e) => handleChange("note", e.target.value)}
                placeholder="Ghi chú"
                rows={4}
                className={styles.formTextarea}
              />
            </div>

            {error && (
              <div className={styles.formField} style={{ color: '#c0392b' }}>
                {error}
              </div>
            )}
            {success && (
              <div className={styles.formField} style={{ color: '#2e7d32' }}>
                {success}
              </div>
            )}

            <div className={styles.formField}>
              <button type="button" className={styles.formSubmit} onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Đang gửi..." : "Xác nhận"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
